import React, { useEffect, useState, SyntheticEvent } from 'react';
import { useTranslation } from "react-i18next";
import {
    Button,
    Card,
    Divider,
    List,
    ListDivider,
    ListItem,
    Grid,
    ListItemContent,
    ListItemDecorator,
    Typography,
    Snackbar,
    SnackbarCloseReason
} from "@mui/joy";


import { Check, Close } from "@mui/icons-material";
import ErrorIcon from '@mui/icons-material/Error';
import './style.scss';
import { getUser } from "../../api/auth";
import Logo from '../../assets/colored_logo.svg';

export interface SubscriptionPlan {
    id: number;
    name: string;
    stripe_plan_id?: string;
    url: string;
    scopes: string[];
    product?: {
        description?: string;
        price?: string;
    };
    style: 'freemium' | 'premium' | 'business';
}

export const VTarifs: React.FC = () => {
    const { t } = useTranslation();

    const [openSnackbar, setOpenSnackbar] = useState(false);

    // Les plans sont statiques, on peut donc les définir en constante
    const plans: SubscriptionPlan[] = [
        {
            id: 1,
            name: 'eevenPro Free',
            stripe_plan_id: 'prod_RVrduTImKl7BVt',
            url: 'https://buy.stripe.com/test_bIY01P1DH1xSbDO5kl',
            scopes: [
                'Gestion complète des Eevents',
                'Performances par zone',
                'Performances par catégorie',
                'Performances d’un Eevent',
                'Votre impact vs le marché',
                '1 boost sponsorisé offert à l’inscription'
            ],
            product: {
                description: 'Accédez gratuitement aux fonctionnalités de base pour démarrer.',
                price: '0€ / mois'
            },
            style: 'freemium'
        },
        {
            id: 2,
            name: 'eevenPro',
            stripe_plan_id: 'prod_QvCsKjCN6DHvGy',
            url: 'https://buy.stripe.com/test_14k6qd5TXdgA5fqaEE',
            scopes: [
                'Toutes les fonctionnalités eevenPro Free',
                'Performances zones & catégories',
                'Performance sur vos Eevents',
                'Eevents similaires',
                'Organisateurs similaires',
                'Audience potentielle',
                'Tendances horaires',
                'Heatmap des Eevents',
                '2 boosts sponsorisés offerts/mois'
            ],
            product: {
                description: 'Débloquez des fonctionnalités avancées sur vos eevents et votre marché.',
                price: '29€ HT / mois'
            },
            style: 'premium'
        },
        {
            id: 3,
            name: 'eevenPro +',
            stripe_plan_id: '',
            scopes: [
                'Toutes les fonctionnalités d’eevenPro',
                'Newsletter aux utilisateurs lors de la publication (1 encart offert par événement publié)',
                'Taux de conversion & temps passé sur la page',
                'Meilleure heure et jour d’interaction',
                'Analyse du public (âge, localisation, préférences)',
                '4 boosts sponsorisés offerts/mois'
            ],
            url: "",
            product: {
                description: 'Une solution complète avec accompagnement personnalisé.',
                price: 'Bientôt disponible'
            },
            style: 'business'
        }
    ];

    // Récupération de l'utilisateur via getUser() (supposé synchrone)
    const user = getUser();
    const userId = user?.id || null;


    // Mise à jour du titre de la page
    useEffect(() => {
        document.title = `EevenPRO - ${t('Tarification')}`;
    }, [t]);

    useEffect(() => {
  console.log("Viewport width:", window.innerWidth);
}, []);


    const handleSelectPlan = (plan: SubscriptionPlan) => {
        if (!plan.stripe_plan_id) {
            setOpenSnackbar(true);
            return;
        }
        window.location.href = `${plan.url}`;
    };

    const handleCloseSnackbar = (
        event: Event | SyntheticEvent<Element, Event> | undefined,
        reason?: SnackbarCloseReason
    ) => {
        if (reason === 'clickaway') return;
        setOpenSnackbar(false);
    };

    return (
        <section id="VTarifs" className="!pb-8">
            <Snackbar
                open={openSnackbar}
                autoHideDuration={3500}
                onClick={handleCloseSnackbar}
                color="danger"
                size="lg"
                variant="solid"
            >
                <ErrorIcon /> Impossible de vous abonner à ce plan pour le moment.
            </Snackbar>
            <Grid container spacing={2} className="!px-8 !pt-4">
                <Grid xs={4} className="login_logo">
                    <img src={Logo} alt="logo colored" />
                    <a href="/">
                        <Typography level="h2" className="firstTitle font-bold">
                            eeven<span className="secondTitle">Pro</span>
                        </Typography>
                    </a>
                </Grid>

                <Grid xs={4} xsOffset={4} className="text-end">
                    <a
                        className="!text-white hover:underline"
                        href="mailto:eeve.contact@gmail.com"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Besoin d'aide ?
                    </a>
                </Grid>

                <Grid xs={12}>
                    <Typography level="h1" className="text-center firstTitle font-bold">
                        Découvrez nos <span className="secondTitle">abonnements</span>
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={3} className="gridTarif joyGridFix">
                <Grid xs={12} className="title-container">
                    <Divider className="divider" />
                </Grid>

                {plans.map((plan) => {
                    const price = plan.product?.price || '0';
                    const description = plan.product?.description || 'Plan sans description';
                    const planName = plan.name.trim();
                    const userHasPlan = user?.subscription_plans?.includes(plan.stripe_plan_id || '');

                    return (
                        <Grid key={plan.id} xs={12} sm={12} md={4}>
                            <Card className={`pricing-card ${plan.style} ${userHasPlan ? 'selected' : ''}`}>
                                <Typography level="h3" className="plan-name">
                                    {planName}
                                </Typography>
                                <Typography level="body-md" className="plan-description">
                                    {description}
                                </Typography>
                                <Typography level="body-md" className="plan-price">
                                    <span className="price">{price}</span>
                                </Typography>

                                <List className="plan-features">
                                    {plan.scopes.map((scope, index) => (
                                        <React.Fragment key={index}>
                                            <ListItem className="feature-item">
                                                <ListItemContent>{scope}</ListItemContent>
                                                <ListItemDecorator>
                                                    <Check />
                                                </ListItemDecorator>
                                            </ListItem>
                                            {index < plan.scopes.length - 1 && <ListDivider />}
                                        </React.Fragment>
                                    ))}
                                </List>

                                <Button
                                    className="subscribe-button"
                                    onClick={() => {
                                        if (!userHasPlan) handleSelectPlan(plan);
                                    }}
                                >
                                    {userHasPlan ? t("Votre plan actuel") : t("Opter pour ce plan")}
                                </Button>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </section>
    );
};